// src/pages/ReturnLabels.js - courier labels for CDNB (breakage/expiry return) bills
import React, { useState, useEffect, useCallback } from 'react';
import { get, post } from '../utils/api';
import { Spinner } from '../components/Shared';

function localDate(d) {
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().split('T')[0];
}

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default function ReturnLabels() {
  const today = localDate(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [q, setQ] = useState('');
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sel, setSel] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (from) p.set('date_from', from);
      if (to) p.set('date_to', to);
      if (q.trim()) p.set('q', q.trim());
      const d = await get('/api/return-labels?' + p.toString());
      setBills((d && d.bills) || []);
      setSel({});
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load');
    } finally { setLoading(false); }
  }, [from, to, q]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const [syncSince, setSyncSince] = useState(localDate(new Date(Date.now() - 7 * 86400000)));
  const [syncMsg, setSyncMsg] = useState('');
  const [syncing, setSyncing] = useState(false);

  async function startSync() {
    if (syncing) return;
    setSyncing(true);
    setSyncMsg('Syncing from Marg... this can take a minute.');
    try {
      await post('/api/return-labels/sync', { since: syncSince });
    } catch (e) {
      setSyncing(false);
      setSyncMsg(e.message || 'Could not start sync');
      return;
    }
    const t0 = Date.now();
    const timer = setInterval(async () => {
      try {
        const st = await get('/api/return-labels/sync-status');
        if (st.state === 'done') {
          clearInterval(timer); setSyncing(false);
          setSyncMsg('Sync finished. Found ' + (st.found == null ? 0 : st.found) + ' bills.');
          load();
        } else if (st.state === 'failed') {
          clearInterval(timer); setSyncing(false);
          setSyncMsg('Sync failed: ' + (st.message || 'unknown error'));
        } else if (Date.now() - t0 > 600000) {
          clearInterval(timer); setSyncing(false);
          setSyncMsg('Sync is taking too long. Check again later.');
        }
      } catch (e) {
        clearInterval(timer); setSyncing(false);
        setSyncMsg(e.message || 'Status check failed');
      }
    }, 3000);
  }

  const selectable = bills.filter(b => b.party_id);
  const allSel = selectable.length > 0 && selectable.every(b => sel[b.vcn]);
  const count = selectable.filter(b => sel[b.vcn]).length;

  function toggleAll() {
    const next = {};
    if (!allSel) selectable.forEach(b => { next[b.vcn] = true; });
    setSel(next);
  }

  function printSelected() {
    const chosen = bills.filter(b => sel[b.vcn] && b.party_id);
    if (!chosen.length) return;
    const groups = new Map();
    chosen.forEach(b => {
      if (!groups.has(b.party_id)) groups.set(b.party_id, { b: b, vcns: [] });
      groups.get(b.party_id).vcns.push(b.vcn);
    });
    const pages = Array.from(groups.values()).map(g => {
      const b = g.b;
      const st = (b.state || '').trim();
      const lines = [b.address_line1, b.address_line2, [b.city, st && st !== '-' ? st : ''].filter(Boolean).join(', '), b.pincode ? 'PIN: ' + b.pincode : ''].filter(x => x && String(x).trim());
      return '<div class="label"><div class="from"><b>FROM:</b> MANGAL DISTRIBUTORS, MYSORE</div>' +
        '<div class="to-h">TO:</div><div class="name">' + esc(b.party_name) + '</div>' +
        '<div class="addr">' + lines.map(esc).join('<br>') + '</div>' +
        (b.phone && b.phone.trim() ? '<div class="mob">Mobile: ' + esc(b.phone) + '</div>' : '') +
        '<div class="bills">Bills: ' + esc(g.vcns.sort().join(', ')) + '</div></div>';
    });
    const html = '<!doctype html><html><head><meta charset="utf-8"><title>Return labels</title><style>' +
      '@page{size:A5;margin:10mm}body{font-family:Arial,sans-serif;margin:0}' +
      '.label{page-break-after:always;padding:6mm;box-sizing:border-box}.label:last-child{page-break-after:auto}' +
      '.from{font-size:14px;border-bottom:2px solid #000;padding-bottom:8px;margin-bottom:18px}' +
      '.to-h{font-size:13px;font-weight:bold;letter-spacing:1px}' +
      '.name{font-size:26px;font-weight:bold;margin:6px 0 8px}' +
      '.addr{font-size:20px;line-height:1.35}' +
      '.mob{font-size:22px;font-weight:bold;margin-top:12px}' +
      '.bills{font-size:15px;margin-top:22px;border-top:1px solid #000;padding-top:8px;word-break:break-word}' +
      '</style></head><body>' + pages.join('') + '</body></html>';
    const w = window.open('', '_blank');
    if (!w) { window.alert('Allow pop-ups for this site to print labels'); return; }
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  }

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold mb-1">Return Labels</h1>
      <p className="text-sm text-gray-500 mb-4">Courier labels for CDNB bills (breakage and expiry returns). One A5 label per party.</p>
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <label className="text-sm">From<br /><input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border rounded px-2 py-1" /></label>
        <label className="text-sm">To<br /><input type="date" value={to} onChange={e => setTo(e.target.value)} className="border rounded px-2 py-1" /></label>
        <label className="text-sm">Bill or party<br /><input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="CDNB00744 or name" className="border rounded px-2 py-1" /></label>
        <button onClick={load} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm">Show</button>
        <label className="text-sm">Load bills since<br /><input type="date" value={syncSince} onChange={e => setSyncSince(e.target.value)} className="border rounded px-2 py-1" /></label>
        <button onClick={startSync} disabled={syncing} className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm disabled:opacity-40">{syncing ? 'Syncing...' : 'Sync from Marg'}</button>
        <button onClick={printSelected} disabled={count === 0} className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm disabled:opacity-40">Print selected ({count})</button>
      </div>
      {syncMsg && <div className="p-3 mb-4 rounded bg-blue-50 text-blue-800 text-sm">{syncMsg}</div>}
      {error && <div className="p-3 mb-4 rounded bg-red-50 text-red-700 text-sm">{error}</div>}
      {loading ? <Spinner /> : (
        <div className="overflow-x-auto border rounded-xl bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="p-2 w-10"><input type="checkbox" checked={allSel} onChange={toggleAll} /></th>
                <th className="p-2">Bill</th>
                <th className="p-2">Date</th>
                <th className="p-2">Party</th>
                <th className="p-2">City</th>
                <th className="p-2">Mobile</th>
              </tr>
            </thead>
            <tbody>
              {bills.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-gray-500">No CDNB bills for these filters.</td></tr>}
              {bills.map(b => (
                <tr key={b.vcn} className="border-t">
                  <td className="p-2"><input type="checkbox" disabled={!b.party_id} checked={!!sel[b.vcn]} onChange={() => setSel(s => ({ ...s, [b.vcn]: !s[b.vcn] }))} /></td>
                  <td className="p-2 font-mono">{b.vcn}</td>
                  <td className="p-2">{b.bill_date ? new Date(b.bill_date).toLocaleDateString('en-IN') : ''}</td>
                  <td className="p-2">{b.party_name || <span className="text-red-600">party {b.party_code} not found</span>}</td>
                  <td className="p-2">{b.city}</td>
                  <td className="p-2">{b.phone && b.phone.trim() ? b.phone : <span className="text-amber-600">no mobile</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
